// T2-2/R2 — **THE ONE-TIME RE-CONSENT.** Shown only when the SERVER says this member owes one, which
// it does only after the owner declares a new Terms version (`TERMS_RECONSENT_VERSION` ships EMPTY,
// so today it prompts nobody). Same checkbox, same documents, same record as signup.
//
// R87 moved it out of App.tsx — the last of the backdrop modals the pointer named, and the only one
// that is not a leaf: it holds THREE hooks, so a gate cannot CALL it. **Nothing is lost by that and
// the reason is worth writing down: inside App.tsx it could not be called either.** What the move
// buys is the same thing every extraction on this line has bought — the consent write is now a file
// something can point at, rather than fifty lines in the middle of a 12,000-line one, and the two
// fixtures that hold the consent law (`terms-consent.mjs`, `r36-legal-pages.mjs`) have a named place
// to read it. R10-3's ruling in the ordinary direction.
//
// **THE COPY IS ENGLISH AND THAT IS DECLARED, NOT AN OVERSIGHT.** It is the same ruling 14-B2 made
// about the signup consent block and for the same reason: **that sentence IS the record.**
// `termsConsent.js` owns `CONSENT_TEXT`, the server writes the version against her row, and both
// documents behind the two links are English. A translated agreement is a second answer key for the
// one thing on this site a court would read.
import { useState } from "react";
import { ModalBackdrop } from "./ModalBackdrop";
import { CONSENT_TEXT, TERMS_VERSION } from "../termsConsent.js";

export function ReconsentModal({ onOpenDoc, onDone }: { onOpenDoc: (doc: "terms" | "privacy") => void; onDone: () => void }) {
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  async function accept() {
    setBusy(true);
    setNote("");
    try {
      const r = await fetch("/api/terms/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ termsAccepted: true, termsVersion: TERMS_VERSION, source: "reconsent" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!d?.ok) { setNote("That could not be saved just now. Please try again."); return; }
      onDone();
    } catch {
      setNote("That could not be saved just now. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalBackdrop label="Our terms have been updated" className="reconsent-backdrop">
      <div className="reconsent-modal">
        <p className="eyebrow">One quick thing</p>
        <h2>Our terms have been updated</h2>
        <p className="reconsent-modal__line">Nothing in your room has changed. Have a read, tick the box, and carry on.</p>
        <div className="consent-block">
          <label className="consent-check">
            <input type="checkbox" name="termsAgreed" aria-label={CONSENT_TEXT} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span className="consent-check__text">
              I have read and agree to the{" "}
              <button type="button" className="consent-link" onClick={() => onOpenDoc("terms")}>Terms of Service</button>
              {" "}and{" "}
              <button type="button" className="consent-link" onClick={() => onOpenDoc("privacy")}>Privacy Policy</button>
            </span>
          </label>
          {note ? <p className="consent-block__note" role="alert">{note}</p> : null}
        </div>
        <button type="button" className="button button--primary" disabled={!agreed || busy} onClick={accept}>
          {busy ? "Saving…" : "Agree and continue"}
        </button>
      </div>
    </ModalBackdrop>
  );
}
